import React from 'react';
import { SlideLayout } from './SlideLayout';
import { motion } from 'framer-motion';
import { FileText, ClipboardCheck, ExternalLink, Lock } from 'lucide-react';
// Drop the hosted PDF URLs here once uploaded. Leave empty to show the
// "coming soon" state. Both should be public URLs (e.g. a CDN link).
const RECRUITING_OUTLINE_DRIVE_ID = '1G2u1XUT362Qv2U_JCPxKd-HtijDIDOVa';
const RECRUITING_RUBRIC_DRIVE_ID = '1Dci8NxHLt2eFdm5_UmDB-Y6zb77yONgl';
function driveUrls(id: string) {
  if (!id) return {
    previewUrl: '',
    viewUrl: ''
  };
  return {
    previewUrl: 'https://drive.google.com/file/d/' + id + '/preview',
    viewUrl: 'https://drive.google.com/file/d/' + id + '/view'
  };
}
interface DocCard {
  icon: typeof FileText;
  eyebrow: string;
  title: string;
  description: string;
  previewUrl: string;
  viewUrl: string;
}
export function Slide_RecruitingDocs() {
  const docs: DocCard[] = [{
    icon: FileText,
    eyebrow: '17-Page Outline',
    title: 'Recruiting Outline',
    description: 'The complete, step-by-step recruiting playbook your team runs, from first touch through hire.',
    ...driveUrls(RECRUITING_OUTLINE_DRIVE_ID)
  }, {
    icon: ClipboardCheck,
    eyebrow: 'Scoring Example',
    title: 'Interview Rubric',
    description: 'A real example of the rubric used to objectively score candidates during interviews.',
    ...driveUrls(RECRUITING_RUBRIC_DRIVE_ID)
  }];
  return <SlideLayout data-id="element-58">
      <div className="flex flex-col h-full justify-center space-y-6" data-id="element-59">
        {/* Header */}
        <div className="text-center space-y-2" data-id="element-60">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.05
        }} className="text-3xl md:text-4xl font-bold text-white" data-id="element-61">
            Systems Scale
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-base text-slate-400 max-w-2xl mx-auto leading-relaxed" data-id="element-62">
            Nothing is left to guesswork. Every campaign runs on a written
            recruiting outline and a structured scoring rubric.
          </motion.p>
        </div>

        {/* Document Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto w-full" data-id="element-63">
          {docs.map((doc, idx) => {
          const hasPdf = doc.viewUrl !== '';
          return <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2 + idx * 0.1
          }} className="bg-slate-900/40 border border-slate-800 rounded-xl p-3 sm:p-4 flex flex-col" data-id="element-64">
                {/* Compact header bar */}
                <div className="flex items-center justify-between px-2 pt-1 pb-3" data-id="element-65">
                  <div className="flex items-center min-w-0" data-id="element-66">
                    <doc.icon className="w-5 h-5 text-[#7FE0C3] flex-shrink-0" data-id="element-67" />
                    <div className="ml-3 min-w-0" data-id="element-68">
                      <h3 className="text-lg font-bold text-white truncate leading-tight" data-id="element-69">
                        {doc.title}
                      </h3>
                      <p className="text-xs text-slate-500 uppercase tracking-wider" data-id="element-70">
                        {doc.eyebrow}
                      </p>
                    </div>
                  </div>
                  {hasPdf && <a href={doc.viewUrl} target="_blank" rel="noopener noreferrer" title="Open full document" className="ml-3 flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-slate-800/60 border border-slate-700 text-slate-400 hover:text-[#7FE0C3] hover:border-[#7FE0C3]/50 transition-colors" data-id="element-71">
                      <ExternalLink className="w-4 h-4" data-id="element-72" />
                    </a>}
                </div>

                {/* Large scrollable preview */}
                <div className="relative rounded-lg overflow-hidden border border-slate-800 bg-slate-950/60 h-[58vh] min-h-[420px]" data-id="element-73">
                  {hasPdf ? <iframe src={doc.previewUrl} title={`${doc.title} preview`} className="w-full h-full" allow="autoplay" data-id="element-74" /> : <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-id="element-75">
                      <Lock className="w-10 h-10 text-slate-600 mb-3" data-id="element-76" />
                      <p className="text-slate-500 text-sm" data-id="element-77">
                        Document preview appears here once the PDF is uploaded.
                      </p>
                    </div>}
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </SlideLayout>;
}